/**
 * Export Engine - Phase 13
 * 
 * Handles CSV, JSON, and report generation for incident data.
 * Client-side export with sanitization.
 */

import { getAllIncidents, applyFilters } from './incidentSelectors';
import { getDomainDisplayLabel, getSourceTypeDisplayLabel } from './incidentModel';

// ==================== CSV EXPORT ====================

/**
 * CSV column definitions
 */
const CSV_COLUMNS = [
  { key: 'incidentId', header: 'Incident ID' },
  { key: 'occurredAt', header: 'Occurred At', formatter: formatTimestamp },
  { key: 'createdAt', header: 'Created At', formatter: formatTimestamp },
  { key: 'resolvedAt', header: 'Resolved At', formatter: formatTimestamp },
  { key: 'severity', header: 'Severity' },
  { key: 'status', header: 'Status' },
  { key: 'sourceType', header: 'Source', formatter: getSourceTypeDisplayLabel },
  { key: 'domain', header: 'Domain', formatter: getDomainDisplayLabel },
  { key: 'eventType', header: 'Event Type' },
  { key: 'title', header: 'Title' },
  { key: 'message', header: 'Message' },
  { key: 'merchantId', header: 'Merchant ID' },
  { key: 'locationId', header: 'Location ID' },
  { key: 'terminalId', header: 'Terminal ID' },
  { key: 'actor.userId', header: 'Actor User ID', formatter: (v, row) => row.actor?.userId || '' },
  { key: 'actor.name', header: 'Actor Name', formatter: (v, row) => row.actor?.name || '' },
  { key: 'relatedEntityType', header: 'Related Entity Type' },
  { key: 'relatedEntityId', header: 'Related Entity ID' },
  { key: 'localOrderId', header: 'Local Order ID' },
  { key: 'serverOrderId', header: 'Server Order ID' },
  { key: 'paymentIntentId', header: 'Payment Intent ID' },
  { key: 'queueItemId', header: 'Queue Item ID' },
  { key: 'managerNote', header: 'Manager Note' },
  { key: 'followUpStatus', header: 'Follow-up Status' },
  { key: 'reviewedBy', header: 'Reviewed By' },
  { key: 'reviewedAt', header: 'Reviewed At', formatter: formatTimestamp },
  { key: 'tags', header: 'Tags', formatter: (v) => Array.isArray(v) ? v.join('; ') : '' }
];

/**
 * Format timestamp for export
 * @param {number} ts
 * @returns {string}
 */
function formatTimestamp(ts) {
  if (!ts) return '';
  return new Date(ts).toISOString();
}

/**
 * Get nested value from object
 * @param {Object} obj
 * @param {string} path
 * @returns {*}
 */
function getNestedValue(obj, path) {
  return path.split('.').reduce((o, p) => (o || {})[p], obj);
}

/**
 * Escape CSV value
 * @param {*} value
 * @returns {string}
 */
function escapeCSV(value) {
  if (value === null || value === undefined) return '';
  const str = String(value);
  // Escape quotes and wrap in quotes if contains comma, quote, or newline
  if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/**
 * Build CSV rows from incidents
 * @param {Array} incidents
 * @returns {string[][]}
 */
export function buildIncidentExportRows(incidents) {
  const rows = [];
  
  // Header row
  rows.push(CSV_COLUMNS.map(col => col.header));
  
  // Data rows
  for (const incident of incidents) {
    const row = CSV_COLUMNS.map(col => {
      let value = getNestedValue(incident, col.key);
      
      if (col.formatter) {
        value = col.formatter(value, incident);
      }
      
      return value;
    });
    rows.push(row);
  }
  
  return rows;
}

/**
 * Convert rows to CSV string
 * @param {string[][]} rows
 * @returns {string}
 */
function rowsToCSV(rows) {
  return rows.map(row => row.map(escapeCSV).join(',')).join('\n');
}

/**
 * Export incidents as CSV
 * @param {Object} filters
 * @returns {Promise<{csv: string, filename: string, count: number}>}
 */
export async function exportIncidentsCsv(filters = {}) {
  const incidents = await getAllIncidents(filters);
  const rows = buildIncidentExportRows(incidents);
  const csv = rowsToCSV(rows);
  const filename = buildIncidentExportFilename(filters, 'csv');
  
  return {
    csv,
    filename,
    count: incidents.length
  };
}

// ==================== JSON EXPORT ====================

/**
 * Sanitize incident for JSON export
 * @param {Object} incident
 * @returns {Object}
 */
function sanitizeIncidentForExport(incident) {
  const sanitized = { ...incident };
  
  // Remove any sensitive fields from metadata
  if (sanitized.metadata) {
    const safeMeta = { ...sanitized.metadata };
    
    // Remove potentially sensitive payload data
    if (safeMeta.payload) {
      const safePayload = { ...safeMeta.payload };
      const sensitiveFields = [
        'pin', 'password', 'token', 'secret', 'api_key', 'apiKey',
        'card_number', 'cardNumber', 'cvv', 'cvc', 'expiry',
        'ssn', 'social_security', 'bank_account', 'routing_number',
        'private_key', 'privateKey', 'credential', 'authorization'
      ];
      
      sensitiveFields.forEach(field => {
        if (safePayload[field]) {
          safePayload[field] = '[REDACTED]';
        }
      });
      
      safeMeta.payload = safePayload;
    }
    
    sanitized.metadata = safeMeta;
  }
  
  return sanitized;
}

/**
 * Export incidents as JSON
 * @param {Object} filters
 * @returns {Promise<{json: string, filename: string, count: number}>}
 */
export async function exportIncidentsJson(filters = {}) {
  const incidents = await getAllIncidents(filters);
  
  // Sanitize each incident
  const sanitizedIncidents = incidents.map(sanitizeIncidentForExport);
  
  const exportData = {
    exportedAt: new Date().toISOString(),
    filters: sanitizeFiltersForExport(filters),
    totalCount: sanitizedIncidents.length,
    incidents: sanitizedIncidents
  };
  
  const json = JSON.stringify(exportData, null, 2);
  const filename = buildIncidentExportFilename(filters, 'json');
  
  return {
    json,
    filename,
    count: incidents.length
  };
}

/**
 * Sanitize filters for export (remove any sensitive data)
 * @param {Object} filters
 * @returns {Object}
 */
function sanitizeFiltersForExport(filters) {
  const safe = { ...filters };
  
  // Convert timestamps to ISO strings
  if (safe.dateFrom) safe.dateFrom = new Date(safe.dateFrom).toISOString();
  if (safe.dateTo) safe.dateTo = new Date(safe.dateTo).toISOString();
  
  return safe;
}

// ==================== FILENAME GENERATION ====================

/**
 * Build export filename based on filters
 * @param {Object} filters
 * @param {string} extension - 'csv' or 'json'
 * @returns {string}
 */
export function buildIncidentExportFilename(filters = {}, extension = 'csv') {
  const parts = ['incident_audit'];
  
  // Add filter context to filename
  if (filters.domain) {
    parts.push(filters.domain);
  }
  if (filters.locationId) {
    parts.push(`loc_${filters.locationId}`);
  }
  if (filters.terminalId) {
    parts.push(`term_${filters.terminalId}`);
  }
  if (filters.severity) {
    parts.push(filters.severity);
  }
  if (filters.unresolvedOnly) {
    parts.push('unresolved');
  }
  
  // Add date range if specified
  if (filters.dateFrom || filters.dateTo) {
    const from = filters.dateFrom ? new Date(filters.dateFrom).toISOString().split('T')[0] : 'start';
    const to = filters.dateTo ? new Date(filters.dateTo).toISOString().split('T')[0] : 'now';
    parts.push(`${from}_to_${to}`);
  }
  
  // Add export timestamp
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
  parts.push(timestamp);
  
  return `${parts.join('_')}.${extension}`;
}

// ==================== DOWNLOAD HELPERS ====================

/**
 * Trigger browser download of content
 * @param {string} content
 * @param {string} filename
 * @param {string} mimeType
 */
export function downloadContent(content, filename, mimeType) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  // Clean up
  setTimeout(() => URL.revokeObjectURL(url), 100);
}

/**
 * Download incidents as CSV
 * @param {Object} filters
 * @returns {Promise<{success: boolean, filename: string, count: number}>}
 */
export async function downloadIncidentsCsv(filters = {}) {
  try {
    const { csv, filename, count } = await exportIncidentsCsv(filters);
    downloadContent(csv, filename, 'text/csv;charset=utf-8');
    return { success: true, filename, count };
  } catch (error) {
    console.error('[ExportEngine] CSV download failed:', error);
    return { success: false, filename: '', count: 0, error: error.message };
  }
}

/**
 * Download incidents as JSON
 * @param {Object} filters
 * @returns {Promise<{success: boolean, filename: string, count: number}>}
 */
export async function downloadIncidentsJson(filters = {}) {
  try {
    const { json, filename, count } = await exportIncidentsJson(filters);
    downloadContent(json, filename, 'application/json');
    return { success: true, filename, count };
  } catch (error) {
    console.error('[ExportEngine] JSON download failed:', error);
    return { success: false, filename: '', count: 0, error: error.message };
  }
}

// ==================== SUMMARY EXPORT ====================

/**
 * Generate a summary report for incidents
 * @param {Object} filters
 * @returns {Promise<Object>}
 */
export async function generateIncidentSummaryReport(filters = {}) {
  const incidents = await getAllIncidents(filters);
  
  const summary = {
    generatedAt: new Date().toISOString(),
    filters: sanitizeFiltersForExport(filters),
    
    counts: {
      total: incidents.length,
      open: incidents.filter(i => ['queued', 'retrying', 'failed', 'conflict', 'manual_review'].includes(i.status)).length,
      resolved: incidents.filter(i => i.status === 'resolved').length,
      failed: incidents.filter(i => i.status === 'failed').length,
      conflict: incidents.filter(i => i.status === 'conflict').length,
      critical: incidents.filter(i => i.severity === 'critical').length
    },
    
    bySeverity: {
      critical: incidents.filter(i => i.severity === 'critical').length,
      error: incidents.filter(i => i.severity === 'error').length,
      warning: incidents.filter(i => i.severity === 'warning').length,
      info: incidents.filter(i => i.severity === 'info').length
    },
    
    byDomain: {},
    bySource: {},
    byLocation: {},
    
    timeline: {
      last24h: 0,
      last7d: 0,
      last30d: 0
    }
  };
  
  const now = Date.now();
  const day = 24 * 60 * 60 * 1000;
  
  for (const incident of incidents) {
    // By domain
    summary.byDomain[incident.domain] = (summary.byDomain[incident.domain] || 0) + 1;
    
    // By source
    summary.bySource[incident.sourceType] = (summary.bySource[incident.sourceType] || 0) + 1;
    
    // By location
    if (incident.locationId) {
      summary.byLocation[incident.locationId] = (summary.byLocation[incident.locationId] || 0) + 1;
    }
    
    // Timeline
    const age = now - incident.occurredAt;
    if (age < day) summary.timeline.last24h++;
    if (age < 7 * day) summary.timeline.last7d++;
    if (age < 30 * day) summary.timeline.last30d++;
  }
  
  return summary;
}

/**
 * Copy summary to clipboard
 * @param {Object} filters
 * @returns {Promise<boolean>}
 */
export async function copySummaryToClipboard(filters = {}) {
  try {
    const summary = await generateIncidentSummaryReport(filters);
    const text = JSON.stringify(summary, null, 2);
    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    console.error('[ExportEngine] Copy to clipboard failed:', error);
    return false;
  }
}

export default {
  // CSV
  exportIncidentsCsv,
  downloadIncidentsCsv,
  buildIncidentExportRows,
  
  // JSON
  exportIncidentsJson,
  downloadIncidentsJson,
  
  // Filename
  buildIncidentExportFilename,
  
  // Download helper
  downloadContent,
  
  // Summary
  generateIncidentSummaryReport,
  copySummaryToClipboard
};
